import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCreateOrganizationRosterEntryMutation } from '@client/graphql/__generated__/main-operations';
import { memo, useCallback, useState } from 'react';
import { useAppContext } from '~/services/main/contexts/AppContext';

interface AddAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const inputStyles = {
  bg: 'whiteAlpha.50',
  border: '1px solid',
  borderColor: 'whiteAlpha.100',
  color: 'white',
  _placeholder: { color: 'whiteAlpha.400' },
  _hover: { borderColor: 'whiteAlpha.200' },
  _focus: {
    borderColor: 'cyan.500',
    boxShadow: 'none',
  },
};

const labelStyles = {
  color: 'white',
  fontSize: 'sm',
  fontWeight: 'medium',
  mb: 2,
};

export const AddAgentModal = memo(function AddAgentModal({
  isOpen,
  onClose,
  onSuccess,
}: AddAgentModalProps) {
  const { selectedOrganization } = useAppContext();
  const toast = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [createRosterEntry, { loading }] =
    useCreateOrganizationRosterEntryMutation();

  const resetForm = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!selectedOrganization?.id) {
      toast({
        title: 'Error',
        description: 'No organization selected',
        status: 'error',
        duration: 3000,
      });

      return;
    }

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({
        title: 'Missing fields',
        description: 'First name, last name, and email are required',
        status: 'warning',
        duration: 3000,
      });

      return;
    }

    try {
      await createRosterEntry({
        variables: {
          input: {
            organizationId: selectedOrganization.id,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phoneNumber: phoneNumber.trim() || undefined,
            isContactable: true,
          },
        },
      });

      toast({
        title: 'Agent added',
        description: `${firstName} ${lastName} has been added to the roster`,
        status: 'success',
        duration: 3000,
      });

      handleClose();
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to add agent',
        status: 'error',
        duration: 5000,
      });
    }
  }, [
    selectedOrganization?.id,
    firstName,
    lastName,
    email,
    phoneNumber,
    createRosterEntry,
    toast,
    handleClose,
    onSuccess,
  ]);

  const isValid =
    firstName.trim() && lastName.trim() && email.trim().includes('@');

  return (
    <Modal isOpen={isOpen} size="md" onClose={handleClose}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="indigo.900" borderRadius="xl" mx={4}>
        <ModalHeader
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
          color="whiteAlpha.600"
          fontSize="xs"
          fontWeight="medium"
          letterSpacing="0.88px"
          pb={4}
          pt={5}
          textTransform="uppercase"
        >
          Add Agent
        </ModalHeader>
        <ModalCloseButton color="whiteAlpha.600" top={4} />

        <ModalBody pb={6} pt={5}>
          <VStack spacing={5}>
            {/* First Name */}
            <FormControl>
              <FormLabel {...labelStyles}>First name</FormLabel>
              <Input
                {...inputStyles}
                placeholder="e.g. John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </FormControl>

            {/* Last Name */}
            <FormControl>
              <FormLabel {...labelStyles}>Last name</FormLabel>
              <Input
                {...inputStyles}
                placeholder="e.g. Smith"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </FormControl>

            {/* Contact Info Section */}
            <Box width="100%">
              <Text color="white" fontSize="sm" fontWeight="medium" mb={1}>
                Contact info
              </Text>
              <Text color="whiteAlpha.600" fontSize="xs" mb={3}>
                We won't contact them without your consent
              </Text>

              <VStack spacing={3}>
                <FormControl>
                  <Input
                    {...inputStyles}
                    placeholder="Phone number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <Input
                    {...inputStyles}
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </Box>

            {/* Submit Button */}
            <Button
              _hover={{ bg: 'cyan.600' }}
              bg="cyan.500"
              color="white"
              isDisabled={!isValid}
              isLoading={loading}
              mt={2}
              size="lg"
              width="100%"
              onClick={() => void handleSubmit()}
            >
              Create
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});
